import React from 'react';
import { TargetIcon, GlobeIcon, SearchIcon, VideoIcon, StarIcon, DatabaseIcon, BotIcon } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';
const sections = [{
  number: '01',
  title: 'Brand Positioning & Messaging',
  description: 'Clear positioning that helps referral partners understand the organisation quickly.',
  items: ['Defined value proposition for solicitors, case managers and clinical referrers', 'Messaging framework tailored to each audience group', 'Clear articulation of specialist neurological rehabilitation expertise', 'Positioning that differentiates NPP Neuro Group from generalist providers', 'Structured explanation of services for NHS, private, insurer and medico-legal pathways', 'Tone of voice principles for professional communications']
}, {
  number: '02',
  title: 'Visual Identity System',
  description: 'A refreshed identity designed to communicate expertise and build trust across all touchpoints.',
  items: ['Logo system including primary mark and variations', 'Typography selection and hierarchy', 'Colour palette with application guidance', 'Visual language and graphic elements', 'Exploration of brand structure including possible naming approach', 'Identity designed for print, digital and presentation use']
}, {
  number: '03',
  title: 'Brand Guidelines',
  description: 'A practical reference document so the team can apply the brand consistently.',
  items: ['Logo usage rules and clear space', 'Typography and colour specifications', 'Tone of voice and messaging guidance', 'Application examples across key formats', 'File structure and asset management guidance']
}, {
  number: '04',
  title: 'Marketing Asset Design',
  description: '',
  items: ['Business cards', 'Email signatures', 'Roll up banners', 'Conference brochures', 'Social media templates', 'Presentation template'],
  note: 'All assets designed to present NPP Neuro Group consistently to solicitors, case managers and referral partners.'
}, {
  number: '05',
  title: 'Referral Partner Positioning',
  description: '',
  items: ['Branded collateral package for referral partners', 'Clear service overview materials', 'Case pathway documentation design', 'Professional presentation of clinical expertise', 'Materials designed for solicitor and case manager audiences']
}, {
  number: '06',
  title: 'Discovery Workshop',
  description: '',
  items: ['Deep dive into organisation structure and services', 'Audience and referral pathway mapping', 'Current brand challenges and opportunities', 'Competitive landscape review']
}];
export function WhatsIncluded() {
  return <section id="included" className="py-20 md:py-28 px-4 md:px-6 bg-surface border-b border-white/[0.06]" data-id="element-335">
      <div className="max-w-7xl mx-auto" data-id="element-336">
        <ScrollReveal data-id="element-337">
          <div className="mb-12 md:mb-16" data-id="element-338">
            <span className="text-accent font-bold tracking-[0.2em] text-xs md:text-sm uppercase block mb-4 md:mb-6" data-id="element-339">
              Deliverables
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 md:mb-8 tracking-tight uppercase leading-[0.9]" data-id="element-340">
              What's
              <br data-id="element-341" />
              included
            </h2>
            <div className="bg-surface-elevated border border-white/[0.06] text-white p-6 md:p-8 max-w-2xl" data-id="element-342">
              <h3 className="text-lg md:text-xl font-bold leading-tight mb-4" data-id="element-343">
                8 to 12 week project to reposition NPP Neuro Group as a trusted
                specialist neurological rehabilitation provider.
              </h3>
              <p className="text-zinc-400 text-sm md:text-base leading-relaxed mb-4" data-id="element-344">
                The current brand reflects the past. This project creates a
                brand that clearly represents the expertise and direction of the
                organisation today.
              </p>
              <p className="text-zinc-400 text-sm md:text-base leading-relaxed mb-4" data-id="element-345">
                When that clarity exists, solicitors, case managers and referral
                partners can understand the organisation quickly and trust its
                expertise. That makes it easier for them to choose NPP Neuro
                Group when cases arise.
              </p>
              <p className="text-zinc-400 text-sm md:text-base leading-relaxed" data-id="element-346">
                Branding does not generate referrals directly. Branding creates
                clarity and professional positioning. That clarity makes it
                easier for referral partners to understand the organisation and
                choose it when cases arise.
              </p>
            </div>
          </div>
        </ScrollReveal>

        {/* Sections grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.06] border border-white/[0.06] mb-px" data-id="element-347">
          {sections.map((section, index) => <ScrollReveal key={section.number} delay={index * 0.06} data-id="element-348">
              <div className="bg-surface p-6 md:p-8 h-full" data-id="element-349">
                <div className="flex items-start gap-4 mb-5" data-id="element-350">
                  <span className="text-xs font-mono text-zinc-600 tracking-widest flex-shrink-0 mt-1" data-id="element-351">
                    [{section.number}]
                  </span>
                  <h3 className="text-base md:text-lg font-bold text-white uppercase tracking-tight" data-id="element-352">
                    {section.title}
                  </h3>
                </div>
                {section.description ? <p className="text-zinc-400 text-sm leading-relaxed mb-5 pl-8" data-id="element-353">
                    {section.description}
                  </p> : null}
                <ul className="space-y-2.5 pl-8" data-id="element-354">
                  {section.items.map((item, i) => <li key={i} className="flex items-start text-sm text-zinc-400" data-id="element-355">
                      <span className="mr-3 text-accent flex-shrink-0 mt-0.5" data-id="element-356">
                        +
                      </span>
                      {item}
                    </li>)}
                </ul>
                {section.note && <p className="mt-5 pl-8 text-xs text-zinc-500 leading-relaxed border-t border-white/[0.06] pt-4" data-id="element-357">
                    {section.note}
                  </p>}
              </div>
            </ScrollReveal>)}
        </div>

        {/* Partner Service Offer */}
        <ScrollReveal delay={0.3} data-id="element-358">
          <div className="border border-accent/30 bg-accent/5 p-6 md:p-8" data-id="element-359">
            <div className="flex flex-col md:flex-row md:items-start gap-6" data-id="element-360">
              <div className="flex-shrink-0" data-id="element-361">
                <div className="flex items-center gap-3 mb-1" data-id="element-362">
                  <BotIcon className="w-5 h-5 text-accent" data-id="element-363" />
                  <span className="text-accent font-bold text-xs uppercase tracking-widest" data-id="element-364">
                    Included at no additional cost
                  </span>
                </div>
                <p className="text-[10px] text-accent/60 font-mono uppercase tracking-widest" data-id="element-365">
                  Value £500 — included within the project
                </p>
              </div>
              <div className="flex-1" data-id="element-366">
                <h3 className="text-lg md:text-xl font-bold text-white uppercase tracking-tight mb-3" data-id="element-367">
                  Brand Structure & Naming Exploration
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed mb-2" data-id="element-368">
                  As part of the discovery process, we will explore the current
                  brand structure and consider whether a naming approach could
                  strengthen clarity for referral partners.
                </p>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6" data-id="element-369">
                  This is not a full naming project. It is a strategic
                  exploration included within the rebrand to ensure the identity
                  and naming work together to communicate expertise clearly.
                </p>
                <h4 className="text-[10px] font-bold text-zinc-500 tracking-[0.2em] uppercase mb-4" data-id="element-370">
                  Included
                </h4>
                <ul className="space-y-2.5 mb-6" data-id="element-371">
                  {['Review of current brand structure and naming', 'Exploration of naming options if appropriate', 'Assessment of how naming supports positioning', 'Recommendations for brand architecture clarity', 'Integration with visual identity direction'].map((item, i) => <li key={i} className="flex items-start text-sm text-zinc-400" data-id="element-372">
                      <span className="mr-3 text-accent flex-shrink-0" data-id="element-373">+</span>
                      {item}
                    </li>)}
                </ul>
                <p className="text-[11px] text-zinc-600 uppercase tracking-widest border-t border-white/[0.06] pt-4" data-id="element-374">
                  Included within the rebrand project scope.
                </p>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>;
}